import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  title?: string;
  avatarUrl?: string;
  count?: number;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 3000,
  onClose,
  title,
  avatarUrl,
  count
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const isClosingRef = useRef(false);

  const handleClose = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setIsVisible(false);
    setTimeout(onClose, 280); // Wait for exit animation to complete
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, message, title, count]);

  const getThemeConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />,
          badgeBg: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400',
          containerBg: 'bg-neutral-900/95 border-emerald-500/30 shadow-[0_12px_32px_rgba(16,185,129,0.15)]',
          progressBar: 'from-emerald-400 to-teal-400',
          titleColor: 'text-emerald-400'
        };
      case 'error':
        return {
          icon: <AlertCircle size={18} className="text-rose-400 shrink-0" />,
          badgeBg: 'bg-rose-500/15 border-rose-500/30 text-rose-400',
          containerBg: 'bg-neutral-900/95 border-rose-500/30 shadow-[0_12px_32px_rgba(244,63,94,0.15)]',
          progressBar: 'from-rose-500 to-pink-500',
          titleColor: 'text-rose-400'
        };
      case 'warning':
        return {
          icon: <AlertTriangle size={18} className="text-amber-400 shrink-0" />,
          badgeBg: 'bg-amber-500/15 border-amber-500/30 text-amber-400',
          containerBg: 'bg-neutral-900/95 border-amber-500/30 shadow-[0_12px_32px_rgba(245,158,11,0.15)]',
          progressBar: 'from-amber-400 to-yellow-400',
          titleColor: 'text-amber-400'
        };
      case 'info':
      default:
        return {
          icon: <Info size={18} className="text-sky-400 shrink-0" />,
          badgeBg: 'bg-sky-500/15 border-sky-500/30 text-sky-400',
          containerBg: 'bg-neutral-900/95 border-sky-500/30 shadow-[0_12px_32px_rgba(14,165,233,0.15)]',
          progressBar: 'from-sky-400 to-indigo-400',
          titleColor: 'text-sky-400'
        };
    }
  };

  const theme = getThemeConfig();

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          layout
          initial={{ opacity: 0, x: 140, scale: 0.92 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{
            opacity: 0,
            x: 140,
            scale: 0.92,
            transition: { duration: 0.22, ease: [0.32, 0, 0.67, 0] }
          }}
          transition={{
            type: 'spring',
            stiffness: 450,
            damping: 28,
            mass: 0.75
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 160 }}
          dragElastic={{ left: 0.05, right: 0.6 }}
          onDragEnd={(_, info) => {
            if (info.offset.x > 50 || info.velocity.x > 200) {
              handleClose();
            }
          }}
          className={`relative overflow-hidden flex items-center gap-3 py-2.5 px-3.5 pr-2.5 rounded-2xl border backdrop-blur-xl max-w-[calc(100vw-2.5rem)] sm:max-w-sm text-white ${theme.containerBg}`}
        >
          {/* Glowing Status / Avatar Badge */}
          <div className="shrink-0 flex items-center justify-center">
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" className="w-7 h-7 rounded-full object-cover ring-1 ring-white/20" />
            ) : (
              <div className={`p-1.5 rounded-xl border ${theme.badgeBg}`}>
                {theme.icon}
              </div>
            )}
          </div>

          {/* Toast Message & Content */}
          <div className="flex-1 min-w-0 pr-1">
            {title && (
              <div className={`text-[11px] font-bold uppercase tracking-wider leading-tight mb-0.5 ${theme.titleColor}`}>
                {title}
              </div>
            )}
            <p className="text-[13px] font-semibold leading-snug tracking-tight text-neutral-100 break-words">
              {typeof count === 'number' && count > 1 ? `+${count} ${message}` : message}
            </p>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleClose}
            className="p-1.5 rounded-xl text-neutral-400 hover:text-white hover:bg-white/10 active:bg-white/20 transition-all shrink-0 cursor-pointer"
            aria-label="Dismiss notification"
          >
            <X size={14} />
          </button>

          {/* 3-Second Subtle Progress Line */}
          <motion.div
            initial={{ scaleX: 1 }}
            animate={{ scaleX: 0 }}
            transition={{ duration: duration / 1000, ease: 'linear' }}
            className={`absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r ${theme.progressBar} origin-left opacity-70`}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
